import { BUILTIN_TYPES, checkPlacement, type NodeType, type StoryCtx } from '@dispositio/core';
import type cytoscape from 'cytoscape';
import { de } from '../i18n/de';
import type { AddMode } from './addable';

/** Werkzeuge der Werkzeugleiste der Handlung: Zeitlinie, Szene und Notizzettel, jeweils neu an der Ablagestelle. */
export type ToolKey = 'timeline' | 'event' | 'note';

export interface ToolDef {
  key: ToolKey;
  label: string;
  mode: AddMode;
}

export const TOOLS: ToolDef[] = [
  { key: 'timeline', label: de.tools.timeline, mode: 'timeline' },
  { key: 'event', label: de.tools.event, mode: 'event' },
  { key: 'note', label: de.tools.note, mode: 'normal' },
];

export const toolByKey = (key: string): ToolDef | undefined => TOOLS.find((t) => t.key === key);

/**
 * Kann das Werkzeug im Node `targetId` benutzt werden (Zeitlinie und Notiz: nur in der Geschichte selbst, Szene: auf der Fläche)?
 * Liefert null oder den Grund (als Hinweis am Icon bzw. beim Ablegen).
 */
export function toolBlocked(ctx: StoryCtx, tool: ToolDef, targetId: string): string | null {
  const target = ctx.nodes.get(targetId);
  if (!target) return de.tools.unavailable;
  if (tool.key === 'timeline') return target.kind === 'story' ? null : de.tools.timelineRoot;
  if (tool.key === 'note') return target.kind === 'story' ? null : de.tools.unavailable;
  if (target.kind === 'timeline') return de.tools.unavailable;
  if (tool.mode === 'event' || tool.mode === 'period') return checkPlacement(ctx, tool.mode, targetId);
  return de.tools.unavailable;
}

/** Bereiche der Bibliothek mit vordefinierter Klasse (ihre Icons heißen wie der Schlüssel). */
export type EntityKey = 'person' | 'place' | 'landscape' | 'organization' | 'object' | 'concept';

/** Vordefinierter Typ (Name) eines Bereichs */
const TYPE_NAME: Record<EntityKey, string> = { person: 'Person', place: 'Ort', landscape: 'Landschaft', organization: 'Organisation', object: 'Gegenstand', concept: 'Konzept' };

/** Vorlage für Klassen, die ältere Projekte noch nicht haben (Gegenstand, Konzept, Landschaft); sie werden beim ersten Gebrauch angelegt. */
export function templateFor(key: EntityKey): Omit<NodeType, 'id' | 'builtin' | 'sort' | 'textual' | 'placeholderId'> | null {
  const t = BUILTIN_TYPES.find((b) => b.name === TYPE_NAME[key]);
  if (!t || (t.category !== 'object' && t.category !== 'place')) return null;
  return { name: t.name, category: t.category, color: t.color, fields: t.fields, titleKey: t.titleKey, hoverKey: t.hoverKey, imageKey: t.imageKey };
}

/** Canvas-Koordinaten (Modellraum) zu einem Punkt im Browserfenster. */
export function modelPoint(cy: cytoscape.Core, clientX: number, clientY: number): { x: number; y: number } {
  const box = cy.container()!.getBoundingClientRect();
  const pan = cy.pan();
  const z = cy.zoom();
  return { x: Math.round((clientX - box.left - pan.x) / z), y: Math.round((clientY - box.top - pan.y) / z) };
}

/** Zeitstrahl unter einem Punkt des Modellraums (etwas Toleranz in y), sonst null. */
export function timelineAt(cy: cytoscape.Core, p: { x: number; y: number }): string | null {
  const margin = 24;
  let found: string | null = null;
  cy.nodes('.axis').forEach((n) => {
    const bb = n.boundingBox({ includeLabels: false, includeOverlays: false, includeEdges: false });
    if (p.x >= bb.x1 && p.x <= bb.x2 && p.y >= bb.y1 - margin && p.y <= bb.y2 + margin) found = String(n.data('nodeId'));
  });
  return found;
}
